import React, { useEffect, useRef, useState } from 'react';
import { PureFrontendVideoPlayer } from '../utils/PureFrontendVideoPlayer';

// Mock数据 - 基于提供的资源信息
const initialMockData = [
  {
    id: 'scene1',
    desc: '一只小蝌蚪在清澈池塘中游泳',
    prompt: '4k ultra realistic, sony A7RIV image of a tiny tadpole swimming in a clear pond, with sunlight filtering through the water and aquatic plants surrounding it.',
    dialogue: '看，一只小蝌蚪正在清澈的池塘中自由自在地游泳，阳光透过水面洒下斑驳的光影。',
    images: ['https://strshrt.xyz/temp/691b14bd924912329f18af2c/image_0.jpeg'],
    videoUrl: '#',
    audioUrl: 'https://strshrt.xyz/temp/691b14bd924912329f18af2c/audio_0.mp3',
    status: 'ready' as const,
    cursorPosition: { startTime: 10, endTime: 20 },
    waveformData: [],
    audioPlayState: false,
    videoLoading: false
  },
  {
    id: 'scene2',
    desc: '小蝌蚪继续游动',
    prompt: 'Close up shot of the tadpole swimming gracefully, showing its delicate tail movements and the ripples in the water.',
    dialogue: '小蝌蚪优雅地游动着，细长的尾巴在水中摆动，荡起一圈圈涟漪。',
    images: ['https://strshrt.xyz/temp/691b14bd924912329f18af2c/image_0.jpeg'],
    videoUrl: 'https://strshrt.xyz/videos/680181f0-c119-4198-9847-6d69d062f801.mp4',
    audioUrl: 'https://strshrt.xyz/temp/690d6ce1924912329f2123d0/audio_1.mp3',
    status: 'ready' as const,
    cursorPosition: { startTime: 0, endTime: 4 },
    waveformData: [],
    audioPlayState: false,
    videoLoading: false
  },
  {
    id: 'scene3',
    desc: '水中植物特写',
    prompt: 'Beautiful aquatic plants with sunlight filtering through water, creating a peaceful underwater scene.',
    dialogue: '周围的水生植物在水中轻轻摇曳，阳光穿过水面形成美丽的光柱。',
    images: ['https://strshrt.xyz/temp/691b14bd924912329f18af2c/image_0.jpeg'],
    videoUrl: '#',
    audioUrl: 'https://strshrt.xyz/temp/691b14bd924912329f18af2c/audio_0.mp3',
    status: 'ready' as const,
    cursorPosition: { startTime: 0, endTime: 3 },
    waveformData: [],
    audioPlayState: false,
    videoLoading: false
  }
];

const PlayerTestPage: React.FC = () => {
  const videoPlayerContainer = useRef<HTMLDivElement>(null);
  const videoPlayerInstance = useRef<PureFrontendVideoPlayer | null>(null);
  const [mockData, setMockData] = useState<string>(JSON.stringify(initialMockData, null, 2));
  const [jsonError, setJsonError] = useState<string>('');

  // 解析JSON数据
  const parseJsonData = () => {
    try {
      const parsed = JSON.parse(mockData);
      setJsonError('');
      return parsed;
    } catch (error) {
      setJsonError(`JSON解析错误: ${error instanceof Error ? error.message : '未知错误'}`);
      return null;
    }
  };

  // 重新初始化播放器
  const reinitializePlayer = () => {
    const sceneList = parseJsonData();
    if (!sceneList || !videoPlayerContainer.current) {
      return;
    }

    console.log('🎬 [PlayerTest] 重新初始化播放器', {
      container: videoPlayerContainer.current,
      sceneCount: sceneList.length
    });

    // 销毁旧实例
    if (videoPlayerInstance.current) {
      videoPlayerInstance.current.destroy();
      videoPlayerInstance.current = null;
    }

    // 创建新播放器实例
    videoPlayerInstance.current = new PureFrontendVideoPlayer(
      videoPlayerContainer.current,
      sceneList
    );
  };

  useEffect(() => {
    // 页面加载时初始化播放器
    reinitializePlayer();

    // 清理函数
    return () => {
      if (videoPlayerInstance.current) {
        videoPlayerInstance.current.destroy();
        videoPlayerInstance.current = null;
      }
    };
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 控制按钮事件处理
  const handlePlay = () => {
    console.log('🎮 [PlayerTest] 点击播放按钮', {
      hasPlayer: !!videoPlayerInstance.current,
      playerInstance: videoPlayerInstance.current
    });

    if (videoPlayerInstance.current) {
      console.log('🎮 [PlayerTest] 调用播放器play方法');
      videoPlayerInstance.current.play();
    } else {
      console.error('🎮 [PlayerTest] 播放器实例不存在');
    }
  };

  const handlePause = () => {
    console.log('🎮 [PlayerTest] 点击暂停按钮');
    if (videoPlayerInstance.current) {
      videoPlayerInstance.current.pause();
    }
  };

  const handleNextScene = () => {
    console.log('🎮 [PlayerTest] 点击下一个场景按钮');
    if (videoPlayerInstance.current) {
      videoPlayerInstance.current.nextScene();
    }
  };

  const handlePlayFromScene = (sceneIndex: number) => {
    console.log('🎮 [PlayerTest] 点击跳转到场景按钮', { sceneIndex });
    if (videoPlayerInstance.current) {
      videoPlayerInstance.current.playFromScene(sceneIndex);
    }
  };

  // JSON变更后重新加载并从指定场景开始播放（使用实例方法）
  const reloadAndPlayFromScene = (sceneIndex: number = 0) => {
    console.log('🎮 [PlayerTest] 重新加载并跳转到场景', { sceneIndex });

    const sceneList = parseJsonData();
    if (!sceneList || !videoPlayerInstance.current) {
      console.error('🎮 [PlayerTest] 无法重新加载播放器：场景数据无效或播放器不存在');
      return;
    }

    try {
      // 使用实例方法，方法内部会处理destroy和重新创建
      const newInstance = videoPlayerInstance.current.reloadAndPlayFromScene(
        sceneList,
        sceneIndex
      );

      // 更新引用
      videoPlayerInstance.current = newInstance;
      console.log('🎮 [PlayerTest] 新播放器实例创建成功');
    } catch (error) {
      console.error('🎮 [PlayerTest] 重新加载播放器失败:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="h-screen flex flex-col">
        {/* 顶部标题栏 */}
        <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
          <h1 className="text-xl font-bold">视频播放器测试页面</h1>
        </div>

        {/* 主内容区域 */}
        <div className="flex-1 flex overflow-hidden">
          {/* 左侧JSON编辑器 */}
          <div className="w-1/2 flex flex-col border-r border-gray-700">
            <div className="bg-gray-800 px-4 py-3 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">JSON数据编辑器</h2>
                <button
                  onClick={reinitializePlayer}
                  className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm transition-colors"
                >
                  🔄 应用并重新加载
                </button>
              </div>
              {jsonError && (
                <div className="mt-2 text-red-400 text-sm">{jsonError}</div>
              )}
            </div>
            <div className="flex-1 p-4 overflow-auto">
              <textarea
                value={mockData}
                onChange={(e) => setMockData(e.target.value)}
                className="w-full h-full bg-gray-900 text-green-400 font-mono text-sm p-4 rounded border border-gray-700 resize-none focus:outline-none focus:border-blue-500"
                placeholder="在这里编辑JSON数据..."
                spellCheck={false}
              />
            </div>
          </div>

          {/* 右侧播放器区域 */}
          <div className="w-1/2 flex flex-col">
            <div className="bg-gray-800 px-4 py-3 border-b border-gray-700">
              <h2 className="text-lg font-semibold">播放器预览</h2>
            </div>
            <div className="flex-1 flex flex-col overflow-auto">
              {/* 控制按钮 */}
              <div className="p-4 bg-gray-800/50 border-b border-gray-700">
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={handlePlay}
                    className="bg-green-600 hover:bg-green-700 px-3 py-2 rounded text-sm transition-colors"
                  >
                    ▶ 播放
                  </button>
                  <button
                    onClick={handlePause}
                    className="bg-yellow-600 hover:bg-yellow-700 px-3 py-2 rounded text-sm transition-colors"
                  >
                    ❚❚ 暂停
                  </button>
                  <button
                    onClick={handleNextScene}
                    className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded text-sm transition-colors"
                  >
                    ⏭ 下一个场景
                  </button>
                  <button
                    onClick={reinitializePlayer}
                    className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded text-sm transition-colors"
                  >
                    🔄 重新初始化
                  </button>
                  <button
                    onClick={() => reloadAndPlayFromScene(1)}
                    className="bg-orange-600 hover:bg-orange-700 px-3 py-2 rounded text-sm transition-colors"
                  >
                    🔄 加载并从场景2开始
                  </button>
                </div>

                {/* 场景跳转按钮 */}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-gray-400 mr-2">场景跳转:</span>
                  {JSON.parse(mockData).map((scene: any, index: number) => (
                    <button
                      key={scene.id}
                      onClick={() => handlePlayFromScene(index)}
                      className="px-2 py-1 rounded text-xs transition-colors bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      场景{index + 1}
                    </button>
                  ))}
                </div>

                {/* reloadAndPlayFromScene 测试区域 */}
                <div className="flex flex-wrap gap-2 items-center border-t border-gray-600 pt-3 mt-3">
                  <span className="text-xs text-gray-400 mr-2">JSON重载测试:</span>
                  <button
                    onClick={() => reloadAndPlayFromScene(0)}
                    className="px-2 py-1 rounded text-xs transition-colors bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    🔄 重载从头播放
                  </button>
                  <button
                    onClick={() => reloadAndPlayFromScene(1)}
                    className="px-2 py-1 rounded text-xs transition-colors bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    🔄 重载从场景2
                  </button>
                  <button
                    onClick={() => reloadAndPlayFromScene(2)}
                    className="px-2 py-1 rounded text-xs transition-colors bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    🔄 重载从场景3
                  </button>
                </div>

                {/* 动态修改JSON测试区域 */}
                <div className="border-t border-gray-600 pt-3 mt-3">
                  <div className="text-xs text-gray-400 mb-2">快速修改JSON测试:</div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button
                      onClick={() => {
                        const modifiedData = JSON.parse(mockData);
                        modifiedData[0].desc = '修改后的场景1描述';
                        setMockData(JSON.stringify(modifiedData, null, 2));
                        reloadAndPlayFromScene(0);
                      }}
                      className="px-2 py-1 rounded text-xs transition-colors bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      📝 修改场景1并重载
                    </button>
                    <button
                      onClick={() => {
                        const modifiedData = JSON.parse(mockData);
                        modifiedData[1].cursorPosition.startTime = 1;
                        modifiedData[1].cursorPosition.endTime = 8;
                        setMockData(JSON.stringify(modifiedData, null, 2));
                        reloadAndPlayFromScene(1);
                      }}
                      className="px-2 py-1 rounded text-xs transition-colors bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      ⏱️ 修改场景2时间并重载
                    </button>
                    <button
                      onClick={() => {
                        const modifiedData = JSON.parse(mockData);
                        modifiedData.push({
                          id: 'scene4',
                          desc: '新增的场景4',
                          prompt: 'A new scene for testing',
                          dialogue: '这是新增的第四个场景',
                          images: ['https://strshrt.xyz/temp/691b14bd924912329f18af2c/image_0.jpeg'],
                          videoUrl: '#',
                          audioUrl: 'https://strshrt.xyz/temp/691b14bd924912329f18af2c/audio_0.mp3',
                          status: 'ready',
                          cursorPosition: { startTime: 2, endTime: 6 },
                          waveformData: [],
                          audioPlayState: false,
                          videoLoading: false
                        });
                        setMockData(JSON.stringify(modifiedData, null, 2));
                        reloadAndPlayFromScene(3);
                      }}
                      className="px-2 py-1 rounded text-xs transition-colors bg-green-600 hover:bg-green-700 text-white"
                    >
                      ➕ 添加场景4并重载
                    </button>
                    <button
                      onClick={() => {
                        setMockData(JSON.stringify(initialMockData, null, 2));
                        reloadAndPlayFromScene(0);
                      }}
                      className="px-2 py-1 rounded text-xs transition-colors bg-red-600 hover:bg-red-700 text-white"
                    >
                      🔄 恢复初始数据
                    </button>
                  </div>
                </div>
              </div>

              {/* 播放器容器 */}
              <div className="flex-1 p-4">
                <div className="w-full h-full max-w-4xl mx-auto">
                  <div
                    className="w-full aspect-video bg-gray-900 border-2 border-gray-700 rounded-lg relative"
                    ref={videoPlayerContainer}
                  >
                    {/* 播放器将在这里渲染 */}
                  </div>
                </div>
              </div>

              {/* 播放器信息 */}
              <div className="p-4 bg-gray-800/30 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  <p>💡 提示：</p>
                  <ul className="mt-1 space-y-1">
                    <li>• 左侧编辑JSON数据后点击"应用并重新加载"按钮</li>
                    <li>• 确保JSON格式正确，包含必要的字段</li>
                    <li>• 播放器支持图片和视频混合播放</li>
                    <li>• 使用场景跳转按钮可快速跳转到指定场景</li>
                    <li>• 点击播放器内的进度条也可跳转到对应场景</li>
                    <li>• 场景跳转会从目标场景开始重新播放</li>
                  </ul>
                  <div className="mt-3 pt-3 border-t border-gray-600">
                    <p>🧪 测试功能说明：</p>
                    <ul className="mt-1 space-y-1">
                      <li>• <span className="text-purple-400">场景跳转</span> - 在当前播放器实例内跳转场景</li>
                      <li>• <span className="text-cyan-400">JSON重载</span> - 重新加载JSON数据并从指定场景开始</li>
                      <li>• <span className="text-indigo-400">快速修改</span> - 一键修改JSON并测试reload功能</li>
                      <li>• <span className="text-green-400">添加场景</span> - 动态添加新场景并重载</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerTestPage;